import { FeatureRequest, FeatureRequestState } from "interfaces/feature-request";
import React from "react";
import styled from "styled-components";
import styles from "styles/styles";
import moment from "moment";
import { useTranslate } from "hooks/common/useTranslate/useTranslate";

const Container = styled.div`
    display: flex;
    width: 100%;
    font-size: 16px;
    font-weight: 400;
`;

const TimelineContainer = styled.div`
    background-color: ${styles.colors.lightGrey};
    flex: 1;
    padding: 1.5rem;
`;

const TimelineDetails = styled.div`
    background-color: ${styles.colors.grey3};
    padding: 1.5rem;
    flex-shrink: 0;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.5rem 1rem;
`;

const ProgressBarContainer = styled.div`
    width: 100%;
    height: 20px;
    background-color: ${styles.colors.grey4};
    border-radius: 5px;
    overflow: hidden;
    margin: 0 0 1rem 0;
`;

const getProgressColor = (state: FeatureRequestState): string => {
    if (state === "RESOLVED") {
        return styles.colors.green5;
    } else if (state === "RECOMMENDED") {
        return styles.colors.grey6;
    } else if (state === "REJECTED") {
        return styles.colors.red;
    } else if (state === "IN_PROGRESS") {
        return styles.colors.grey5;
    } else {
        return styles.colors.grey5;
    }
};

const ProgressBarFill = styled.div<{ percentage: number; state: FeatureRequestState }>`
    height: 100%;
    width: ${(props) => props.percentage}%;
    background-color: ${(props) => getProgressColor(props.state)};
    transition: width 0.3s ease-in-out, background-color 0.3s ease-in-out;
`;

const ProgressLabels = styled.div`
    position: relative;
    margin: 0.5rem 0 0 0;
    height: 20px;
`;

const ProgressLabel = styled.span<{ position: number; isFirst?: boolean; isLast?: boolean }>`
    position: absolute;
    left: ${(props) => props.position}%;
    transform: ${(props) =>
        props.isFirst ? "translateX(0)" : props.isLast ? "translateX(-100%)" : "translateX(-50%)"};
    font-size: 14px;
    white-space: nowrap;
`;

const ProgressBarHeader = styled.p`
    font-weight: 400;
    font-size: 16px;
    margin: 0 0 0.25rem 0;
`;

const calculateReadyPercentage = (featureRequest: FeatureRequest): number => {
    if (featureRequest.state === "RESOLVED") {
        return 100;
    } else if (featureRequest.state === "RECOMMENDED" || featureRequest.state === "REJECTED") {
        return 66.66;
    } else if (featureRequest.state === "IN_PROGRESS") {
        return 33.33;
    } else {
        return 0;
    }
};

const Timeline: React.FC<{
    featureRequest: FeatureRequest;
}> = ({ featureRequest }) => {
    const translate = useTranslate();

    const getLabelsWithTranslations = (featureRequest: FeatureRequest) =>
        featureRequest.state === "REJECTED"
            ? [
                  { label: translate("featureRequests.detailsTimeline.new"), position: 0 },
                  {
                      label: translate("featureRequests.detailsTimeline.comingToProcessing"),
                      position: 33.33,
                  },
                  { label: translate("featureRequests.detailsTimeline.rejected"), position: 66.66 },
              ]
            : [
                  { label: translate("featureRequests.detailsTimeline.new"), position: 0 },
                  {
                      label: translate("featureRequests.detailsTimeline.comingToProcessing"),
                      position: 33.33,
                  },
                  {
                      label: translate("featureRequests.detailsTimeline.preliminaryApproval"),
                      position: 66.66,
                  },
                  { label: translate("featureRequests.detailsTimeline.approved"), position: 100 },
              ];

    const createdAt = moment.tz(featureRequest.createdAt, "Europe/Helsinki");
    const updatedAt = moment.tz(featureRequest.updatedAt, "Europe/Helsinki");
    const nextOrLastMeeting = moment.tz(featureRequest.nextOrLastMeetingDate, "Europe/Helsinki");
    const documentationCommentDate = moment.tz(
        featureRequest.documentationCommentDate,
        "Europe/Helsinki"
    );

    return (
        <Container>
            <TimelineContainer>
                <ProgressBarHeader>
                    {translate("featureRequests.detailsTimeline.phase")}
                </ProgressBarHeader>
                <ProgressBarContainer>
                    <ProgressBarFill
                        percentage={calculateReadyPercentage(featureRequest)}
                        state={featureRequest.state}
                    />
                </ProgressBarContainer>
                <ProgressLabels>
                    {getLabelsWithTranslations(featureRequest).map((item, index) => (
                        <ProgressLabel
                            key={index}
                            position={item.position}
                            isFirst={index === 0}
                            isLast={index === 3}
                        >
                            {item.label}
                        </ProgressLabel>
                    ))}
                </ProgressLabels>
            </TimelineContainer>
            <TimelineDetails>
                <span>{translate("featureRequests.detailsTimeline.createdAt")}</span>
                <span>{createdAt.isValid() ? moment(createdAt).format("DD.MM.YYYY") : "-"}</span>

                <span>{translate("featureRequests.detailsTimeline.lastUpdated")}</span>
                <span>{updatedAt.isValid() ? updatedAt.format("DD.MM.YYYY") : "-"}</span>

                <span>{translate("featureRequests.detailsTimeline.meetingDate")}</span>
                <span>
                    {nextOrLastMeeting.isValid() ? nextOrLastMeeting.format("DD.MM.YYYY") : "-"}
                </span>

                <span>{translate("featureRequests.detailsTimeline.comments")}</span>
                <span>
                    {documentationCommentDate.isValid()
                        ? documentationCommentDate.format("DD.MM.YYYY")
                        : "-"}
                </span>
            </TimelineDetails>
        </Container>
    );
};

export default Timeline;
